import L from 'leaflet';
import { hexToRgba } from '../../util/ColorUtil';
import poiicons from '../../resources/generated/poiicons.json';
import mapicons from '../../resources/generated/mapicons.json';
import poiTypes from '../../resources/poiStyles/poi-types.json';
import poiCategoriesData from '../../resources/poiStyles/poi-categories.json';
import backgrounds from '../../resources/generated/poiBackgroundIcons.json';
import PoiManager from '../../manager/PoiManager';
import { startPointIcon } from './StartPointMarker';
import { intermediatePointIcon } from './IntermediatePointMarker';
import { destinationPointIcon } from './DestinationPointMarker';

const poiIconsSet = new Set(poiicons);
const mapIconsSet = new Set(mapicons);

const poiTypeFallbackMap = Object.fromEntries(
    poiTypes.filter((pt) => pt.tag && pt.value).map((pt) => [pt.name, `${pt.tag}_${pt.value}`])
);

export const BACKGROUND_WPT_SHAPE_CIRCLE = 'circle';
export const BACKGROUND_WPT_SHAPE_OCTAGON = 'octagon';
export const BACKGROUND_WPT_SHAPE_SQUARE = 'square';
const DEFAULT_WPT_ICON = 'special_star';
export const DEFAULT_WPT_COLOR = '#eecc22';
export const DEFAULT_POI_COLOR = '#fe8800';

export const DEFAULT_ICON_SIZE = 24;

export const POI_ICONS_FOLDER = 'poi-icons-svg';
export const MAP_ICONS_FOLDER = 'map-icons-svg';
export const POI_ICON_TYPE = 'poi';
export const MAP_ICON_TYPE = 'map';
export const ICONS_PREFIX = 'mx_';
export const COLORED_ICONS_PREFIX = 'c_mx_';

const MarkerIcon = ({ iconType = 'default-marker', bg = 'blue' }) => {
    let svg =
        `<svg class="background" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">` +
        `<circle cx="24" cy="24" r="15" fill='${bg}'/>` +
        `</svg>`;

    return L.divIcon({
        html: `<div>${svg}<img class="icon" src="/map/images/map_icons/${iconType}.svg"></div>`,
    });
};

const options = {
    startIcon: startPointIcon,
    interIcon: intermediatePointIcon,
    endIcon: destinationPointIcon,
    pointerIcons: MarkerIcon({ bg: '#fec93b' }),
    pointerGraph: L.icon({
        iconUrl: '/map/images/map_icons/circle.svg',
        iconSize: [16, 16],
        clickable: false,
    }),
    route: L.icon({
        iconUrl: '/map/images/map_icons/circle.svg',
        iconSize: [13, 13],
        clickable: false,
    }),
    trackStart: L.icon({
        iconUrl: '/map/images/map_icons/map_track_point_start.svg',
        iconSize: [60, 60],
        clickable: false,
    }),
    trackEnd: L.icon({
        iconUrl: '/map/images/map_icons/map_track_point_finish.svg',
        iconSize: [60, 60],
        clickable: false,
    }),
    trackAnalyzerPointA: L.icon({
        iconUrl: '/map/images/map_icons/ic_action_point_a_colored.svg',
        iconSize: [30, 30],
        clickable: false,
    }),
    trackAnalyzerPointB: L.icon({
        iconUrl: '/map/images/map_icons/ic_action_point_b_colored.svg',
        iconSize: [30, 30],
        clickable: false,
    }),
};

export function createPoiIcon({
    point = null,
    color,
    background,
    hasBackgroundLight = true,
    icon = null,
    svgIcon = null,
    folder = POI_ICONS_FOLDER,
    iconSize = 12,
    backgroundSize = null,
}) {
    let colorBackground =
        color && color !== 'null'
            ? color
            : point?.extensions?.color && point?.extensions.color !== 'null'
              ? point?.extensions.color
              : DEFAULT_WPT_COLOR;
    colorBackground = hexToRgba(colorBackground);
    const shapeBackground = background ?? point?.background;
    let svg = getBackground(colorBackground, shapeBackground);
    if (backgroundSize) {
        svg = changeIconSizeWpt(svg, 48, 48);
    }
    if (svg) {
        const idString = `id="se-poi-marker-background-${color}-${background}"`;
        svg = svg.replace('<svg ', `<svg ${idString} `);
    }
    const svgLight = hasBackgroundLight ? getBackgroundLight(shapeBackground) : '';
    const iconWpt =
        icon && icon !== 'null'
            ? icon
            : point?.extensions?.icon && point?.extensions.icon !== 'null'
              ? point?.extensions.icon
              : DEFAULT_WPT_ICON;
    let part = point ? 'mx_' : '';
    let html;
    const allIconSize = backgroundSize ? backgroundSize : DEFAULT_ICON_SIZE;
    const bsize = backgroundSize ? backgroundSize : getBackgroundSize(shapeBackground);
    const isize = iconSize;
    const offsetX = (allIconSize - isize) / 2; // Center the image horizontally
    const offsetY = (allIconSize - isize) / 2; // Center the image vertically
    if (iconWpt || svgIcon) {
        let iconElement;
        if (svgIcon) {
            iconElement = svgIcon
                .replace(/width="\d+"/, `width="${isize}"`)
                .replace(/height="\d+"/, `height="${isize}"`);
        } else {
            iconElement = `<image width="${isize}" height="${isize}" href="/map/images/${folder}/${part}${iconWpt}.svg" />`;
        }

        html = `<svg viewBox="0 0 ${allIconSize} ${allIconSize}" style="filter: drop-shadow(0px 2px 7px rgba(0, 0, 0, 0.10)) drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.23));" width="${allIconSize}" height="${allIconSize}" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(${(allIconSize - bsize) / 2}, ${(allIconSize - bsize) / 2})">
                ${svg}
            </g>
            <g transform="translate(${(allIconSize - bsize) / 2}, ${(allIconSize - bsize) / 2})">
                ${svgLight}
            </g>
            <g transform="translate(${offsetX}, ${offsetY})">
                ${iconElement}
            </g>
            </svg>`;
    } else {
        html = `<svg viewBox="0 0 ${allIconSize} ${allIconSize}" style="filter: drop-shadow(0px 2px 7px rgba(0, 0, 0, 0.10)) drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.23));" xmlns="http://www.w3.org/2000/svg">
                   <g transform="translate(${(allIconSize - bsize) / 2}, ${(allIconSize - bsize) / 2})">
                      ${svg}
                   </g>
                   <g transform="translate(${(allIconSize - bsize) / 2}, ${(allIconSize - bsize) / 2})">
                      ${svgLight}
                   </g>
                   <image width="${isize}" height="${isize}" href="/map/images/${POI_ICONS_FOLDER}/${ICONS_PREFIX}${DEFAULT_WPT_ICON}.svg" />
                   </svg>`;
    }
    return L.divIcon({
        html,
        iconSize: [allIconSize, allIconSize],
        iconAnchor: [allIconSize / 2, allIconSize / 2],
    });
}

export function getPoiCategoryIcon({ icon, color, background }) {
    const colorBackground = hexToRgba(color);
    const svg = getBackground(colorBackground, background);
    const allIconSize = DEFAULT_ICON_SIZE;
    const bsize = getBackgroundSize(background);
    const isize = 18;
    const offsetX = (allIconSize - isize) / 2; // Center the image horizontally
    const offsetY = (allIconSize - isize) / 2; // Center the image vertically
    const src = getIconUrlByName(POI_ICON_TYPE, PoiManager.preparePoiFilterIcon(icon));

    const html = `<svg viewBox="0 0 ${allIconSize} ${allIconSize}" width="${bsize}" height="${bsize}" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(${(allIconSize - bsize) / 2}, ${(allIconSize - bsize) / 2})">
                ${svg}
            </g>
            <image x="${offsetX}" y="${offsetY}" width="${isize}" height="${isize}" href="${src}" />
            </svg>`;

    return L.divIcon({ html: html });
}

function isStrangeShape(shape) {
    return (
        shape !== BACKGROUND_WPT_SHAPE_CIRCLE &&
        shape !== BACKGROUND_WPT_SHAPE_OCTAGON &&
        shape !== BACKGROUND_WPT_SHAPE_SQUARE
    );
}

export function getBackground(colorBackground, shape) {
    let svg;
    if (shape) {
        if (shape === BACKGROUND_WPT_SHAPE_CIRCLE || isStrangeShape(shape)) {
            svg = changeSvgColor(backgrounds['bg_point_circle.svg'], colorBackground);
        }
        if (shape === BACKGROUND_WPT_SHAPE_OCTAGON) {
            svg = changeSvgColor(backgrounds['bg_point_octagon.svg'], colorBackground);
        }
        if (shape === BACKGROUND_WPT_SHAPE_SQUARE) {
            svg = changeSvgColor(backgrounds['bg_point_square.svg'], colorBackground);
        }
    } else {
        svg = changeSvgColor(backgrounds['bg_point_circle.svg'], colorBackground);
    }
    return svg;
}

function getBackgroundLight(shape) {
    let svg;
    if (shape) {
        if (shape === BACKGROUND_WPT_SHAPE_CIRCLE || isStrangeShape(shape)) {
            svg = backgrounds['bg_point_circle_light.svg'];
        }
        if (shape === BACKGROUND_WPT_SHAPE_OCTAGON) {
            svg = backgrounds['bg_point_octagon_light.svg'];
        }
        if (shape === BACKGROUND_WPT_SHAPE_SQUARE) {
            svg = backgrounds['bg_point_square_light.svg'];
        }
    } else {
        svg = backgrounds['bg_point_circle_light.svg'];
    }
    return svg;
}

function getBackgroundSize(shape) {
    let res;
    if (shape) {
        if (shape === BACKGROUND_WPT_SHAPE_CIRCLE || isStrangeShape(shape)) {
            res = parseSvgSize(backgrounds['bg_point_circle_light.svg']);
        }
        if (shape === BACKGROUND_WPT_SHAPE_OCTAGON) {
            res = parseSvgSize(backgrounds['bg_point_octagon_light.svg']);
        }
        if (shape === BACKGROUND_WPT_SHAPE_SQUARE) {
            res = parseSvgSize(backgrounds['bg_point_square_light.svg']);
        }
    } else {
        res = parseSvgSize(backgrounds['bg_point_circle_light.svg']);
    }
    if (res) {
        return res.width;
    }
}

export function removeShadowFromIconWpt(svgHtml) {
    const filterPattern = /filter=".*?"/g;
    const styleFilterPattern = /style="[^"]*filter:[^;"]*;?[^"]*"/g;

    svgHtml = svgHtml.replace(filterPattern, '');

    svgHtml = svgHtml.replace(styleFilterPattern, (match) => {
        return match.replace(/filter:[^;"]*;?/, '');
    });
    return svgHtml;
}

export function changeIconColor(svgHtml, color) {
    if (!svgHtml) {
        return svgHtml;
    }
    const colorPattern = /(<path[^>]*fill=")[^"]*(")/g;
    const strokePattern = /(<path[^>]*stroke=")[^"]*(")/g;
    svgHtml = svgHtml.replace(colorPattern, `$1${color}$2`);
    svgHtml = svgHtml.replace(strokePattern, `$1${color}$2`);
    return svgHtml;
}

export function changeSvgColor(svgHtml, color) {
    const colorPattern = /(fill=")(?!none")[^"]*(")/g;
    return svgHtml.replace(colorPattern, `$1${color}$2`);
}

function parseSvgSize(svgHtml) {
    const viewBoxPattern = /<svg[^>]*viewBox="0 0 (\d+) (\d+)"/;
    const viewBoxMatch = svgHtml.match(viewBoxPattern);
    const width = viewBoxMatch ? parseInt(viewBoxMatch[1], 10) : null;
    const height = viewBoxMatch ? parseInt(viewBoxMatch[2], 10) : null;
    if (width && height) {
        return { width, height };
    }
    return null;
}

function replacePathDataAndCalculateSize(pathData, shapeSize, oldShapeSize) {
    const scaleFactor = shapeSize / oldShapeSize;
    // 1..7 inside A/a: 1-2 radii, 3 x-axis rotation, 4-5 flags, 6-7 the point. Only the radii and
    // the point are sizes. One A may carry several sets of them, the 8th number starts a new set.
    let arcN = 0;
    const newPathData = pathData.replace(/([Aa])|([MLHVCSQTZmlhvcsqtz])|([-+]?\d*\.?\d+)/g, (m, isArc, isCmd, num) => {
        if (isArc) {
            arcN = 1;
            return m;
        }
        if (isCmd) {
            arcN = 0;
            return m;
        }
        if (num !== undefined) {
            const keepAsIs = arcN >= 3 && arcN <= 5;
            if (arcN >= 1) {
                arcN = arcN === 7 ? 1 : arcN + 1;
            }
            if (keepAsIs) {
                return num;
            }
            const scaled = Number.parseFloat(num) * scaleFactor;
            return String(Math.round(scaled * 100) / 100);
        }
        return m;
    });
    return { newPathData, realWidth: shapeSize, realHeight: shapeSize };
}

// only for icons with image (with href)
export function changeIconSizeWpt(svgHtml, iconSize, shapeSize, shape = null) {
    // Update the sizes inside viewBox and for <image>, <circle>, <path>, <rect>
    const viewBoxPattern = /viewBox="0 0 (\d+) (\d+)"/;
    // the space keeps stroke-width out: it is scaled below, not set to the size of the shape
    const widthPattern = /(\s)width="(\d+)"/;
    const heightPattern = /(\s)height="(\d+)"/;
    const circlePattern = /<circle ([^>]*)cx="(\d+\.?\d*)" ([^>]*)cy="(\d+\.?\d*)" ([^>]*)r="(\d+\.?\d*)" ([^>]*)\/>/;
    const pathPattern = /<path\s[^>]*d="([^"]+)"[^>]*>/g;
    const rectPattern =
        /<rect ([^>]*)width="(\d+\.?\d*)" ([^>]*)height="(\d+\.?\d*)" ([^>]*)rx="(\d+\.?\d*)" ([^>]*)\/>/;
    const imagePattern = /<g[^>]*>\s*<image[^>]*\/>\s*<\/g>/g;
    const singleImagePattern = /<image[^>]*\/>/g;

    const viewBoxMatch = svgHtml.match(viewBoxPattern);
    const oldShapeSize = viewBoxMatch ? parseFloat(viewBoxMatch[1]) : shapeSize;
    let nestedBWidth = shapeSize;
    let nestedBHeight = shapeSize;

    svgHtml = svgHtml.replace(viewBoxPattern, () => {
        // Update the sizes inside viewBox
        return `viewBox="0 0 ${shapeSize} ${shapeSize}"`;
    });

    // Remove <g> wrapper around <image> if it exists and process the <image>
    svgHtml = svgHtml.replace(imagePattern, (match) => {
        const imageMatch = match.match(singleImagePattern);
        return imageMatch ? imageMatch[0] : match;
    });

    svgHtml = svgHtml.replace(widthPattern, (match, before) => {
        // Update width
        return `${before}width="${shapeSize}"`;
    });

    svgHtml = svgHtml.replace(heightPattern, (match, before) => {
        // Update height
        return `${before}height="${shapeSize}"`;
    });

    // Update the sizes inside <circle>, <path>, <rect>
    svgHtml = svgHtml.replace(
        /<svg ([^>]*)width="(\d+\.?\d*)" ([^>]*)height="(\d+\.?\d*)" ([^>]*)viewBox="0 0 (\d+\.?\d*) (\d+\.?\d*)"([^>]*)>/g,
        (match, prefix, nestedWidth, middle1, nestedHeight, middle2, viewBoxWidth, viewBoxHeight, suffix) => {
            if (match.includes('<image')) {
                return match;
            }
            return `<svg ${prefix} width="${shapeSize}" ${middle1} height="${shapeSize}" ${middle2} viewBox="0 0 ${shapeSize} ${shapeSize}"${suffix}>`;
        }
    );

    svgHtml = svgHtml.replace(circlePattern, (match, prefix, cx, middle, cy, middle2, r, suffix) => {
        // Update the sizes inside <circle>
        const newCx = shapeSize / 2;
        const newCy = shapeSize / 2;
        const newR = shapeSize / 2;
        return `<circle ${prefix} cx="${newCx}" ${middle} cy="${newCy}" ${middle2} r="${newR}" ${suffix}/>`;
    });

    const scaleFactor = shapeSize / oldShapeSize;
    svgHtml = svgHtml.replace(pathPattern, (match, pathData) => {
        const { newPathData, realWidth, realHeight } = replacePathDataAndCalculateSize(
            pathData,
            shapeSize,
            oldShapeSize
        );
        nestedBHeight = realHeight;
        nestedBWidth = realWidth;
        // Preserve all path attributes (fill-rule, clip-rule, fill, stroke, etc.), only replace d
        return match.replace(/d="[^"]*"/, `d="${newPathData}"`);
    });

    // Scale stroke-width so thick strokes (e.g. 33 in 580 viewBox) don't fill the whole icon in 36 viewBox
    svgHtml = svgHtml.replace(/stroke-width="([\d.]+)"/g, (_, w) => {
        const scaled = parseFloat(w) * scaleFactor;
        return `stroke-width="${Math.max(0.5, Math.round(scaled * 100) / 100)}"`;
    });

    svgHtml = svgHtml.replace(rectPattern, (match, prefix, width, middle, height, middle2, rx, suffix) => {
        // Update the sizes inside <rect>
        const newWidth = shapeSize;
        const newHeight = shapeSize;
        const newRx = newWidth / 8;
        return `<rect ${prefix} width="${newWidth}" ${middle} height="${newHeight}" ${middle2} rx="${newRx}" ${suffix}/>`;
    });

    svgHtml = svgHtml.replace(/<g transform="translate\(([-\d.]+),\s*([-\d.]+)\)">/g, (match, x, y) => {
        if (shape === BACKGROUND_WPT_SHAPE_OCTAGON) {
            const newX = Math.round(Number.parseFloat(x) * scaleFactor * 100) / 100;
            const newY = Math.round(Number.parseFloat(y) * scaleFactor * 100) / 100;
            return `<g transform="translate(${newX}, ${newY})">`;
        }
        const offsetX = (shapeSize - nestedBWidth) / 2;
        const offsetY = (shapeSize - nestedBHeight) / 2;
        return `<g transform="translate(${offsetX}, ${offsetY})">`;
    });

    // Process single <image> tags
    svgHtml = svgHtml.replace(singleImagePattern, (match) => {
        const hrefPattern = /href="([^"]*)"/;
        const hrefMatch = match.match(hrefPattern);
        const href = hrefMatch ? hrefMatch[1] : '';

        const newSize = iconSize;
        const offsetX = (shapeSize - newSize) / 2;
        const offsetY = (shapeSize - newSize) / 2;
        const transform = `translate(${offsetX},${offsetY})`;

        return `<image transform="${transform}" width="${newSize}" height="${newSize}" href="${href}" />`;
    });

    return svgHtml;
}

// Resolves a poi_type name to the icon key that has an SVG available.
export function resolvePoiIconKey(name) {
    if (poiIconsSet.has(`${ICONS_PREFIX}${name}.svg`)) {
        return name;
    }
    const fallback = poiTypeFallbackMap[name];
    if (fallback && poiIconsSet.has(`${ICONS_PREFIX}${fallback}.svg`)) {
        return fallback;
    }
    return null;
}

// Pre-computed resolved icon categories: each category's raw icon names are resolved
// and filtered to only those with an available SVG.
export const resolvedPoiCategories = (() => {
    const result = {};
    const cats = poiCategoriesData?.categories;
    if (!cats) return result;
    for (const [category, data] of Object.entries(cats)) {
        const resolved = (data.icons ?? []).map(resolvePoiIconKey).filter(Boolean);
        if (resolved.length > 0) {
            result[category] = resolved;
        }
    }
    return result;
})();

export function getIconUrlByName(type, name) {
    if (type === POI_ICON_TYPE) {
        if (poiIconsSet.has(`${ICONS_PREFIX}${name}.svg`)) {
            return `/map/images/${POI_ICONS_FOLDER}/${ICONS_PREFIX}${name}.svg`;
        } else return `/map/images/${POI_ICONS_FOLDER}/${COLORED_ICONS_PREFIX}${name}.svg`;
    } else if (type === MAP_ICON_TYPE) {
        if (mapIconsSet.has(`${ICONS_PREFIX}${name}.svg`)) {
            return `/map/images/${MAP_ICONS_FOLDER}/${ICONS_PREFIX}${name}.svg`;
        } else return `/map/images/${MAP_ICONS_FOLDER}/${COLORED_ICONS_PREFIX}${name}.svg`;
    }
    return '';
}

const MarkerOptions = {
    options,
    BACKGROUND_WPT_SHAPE_CIRCLE: BACKGROUND_WPT_SHAPE_CIRCLE,
    BACKGROUND_WPT_SHAPE_OCTAGON: BACKGROUND_WPT_SHAPE_OCTAGON,
    BACKGROUND_WPT_SHAPE_SQUARE: BACKGROUND_WPT_SHAPE_SQUARE,
    DEFAULT_WPT_ICON: DEFAULT_WPT_ICON,
    DEFAULT_WPT_COLOR: DEFAULT_WPT_COLOR,
};

export default MarkerOptions;
